/** "Invoices" and "Papers": what they owe with a pay link, and papers already sent to them. */
import React from "react";
import { FileText } from "lucide-react";
import { openMyDocument, useMyDocuments, useMyInvoices } from "@/services/python-api/customerHooks";
import { Card, Empty, Loading, Page, Pill, day } from "./parts";

const TONE = {
  due: "bg-[#FEF6E7] text-[#865503]",
  "part paid": "bg-[#FEF6E7] text-[#865503]",
  overdue: "bg-[#FBEAE9] text-[#9E0A05]",
  paid: "bg-[#E7F6EC] text-[#036B26]",
};

export function MyInvoices() {
  const { data, isLoading } = useMyInvoices();
  const invoices = data?.invoices ?? [];
  return (
    <Page title="Invoices">
      {isLoading ? (
        <Loading />
      ) : invoices.length === 0 ? (
        <Empty title="No invoices yet" />
      ) : (
        <div className="space-y-3">
          {invoices.map((inv) => (
            <Card key={inv.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[15px] font-semibold text-[#101928]">{inv.number}</p>
                  <p className="text-[13px] text-[#667185]">
                    {inv.car} {inv.cars.length ? `· …${inv.cars.join(", …")}` : ""}
                  </p>
                </div>
                <Pill tone={TONE[inv.status]}>{inv.status === "part paid" ? "Part paid" : inv.status[0].toUpperCase() + inv.status.slice(1)}</Pill>
              </div>
              <div className="mt-3 divide-y divide-[#F0F2F5]">
                {inv.lines.map((l) => (
                  <div key={l.what} className="flex justify-between py-1.5 text-[14px]">
                    <span className="text-[#475367]">{l.what}</span>
                    <span className="text-[#1D2739]">{l.amount}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2 text-[14px] font-semibold">
                  <span>Balance</span>
                  <span>{inv.balance}</span>
                </div>
              </div>
              <p className="text-[12px] text-[#667185]">
                {inv.due_on ? `Due ${day(inv.due_on)}` : ""} {inv.paid !== "$0" && inv.paid !== "₦0" ? `· Paid ${inv.paid}` : ""}
              </p>
              <div className="mt-3 flex gap-2">
                {inv.pay_link ? (
                  <a
                    href={inv.pay_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-11 flex-1 items-center justify-center rounded-full bg-[#2563EB] text-[15px] font-medium text-white"
                  >
                    Pay {inv.balance}
                  </a>
                ) : null}
                <a
                  href={inv.view_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-11 flex-1 items-center justify-center rounded-full border border-[#D0D5DD] text-[15px] font-medium text-[#344054]"
                >
                  View invoice
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}

export function MyDocuments() {
  const { data, isLoading } = useMyDocuments();
  const docs = data?.documents ?? [];
  const uploads = data?.uploads ?? [];
  return (
    <Page title="Papers">
      {isLoading ? (
        <Loading />
      ) : docs.length === 0 && uploads.length === 0 ? (
        <Empty title="No papers yet">Bills of lading, dock receipts and titles we send you appear here.</Empty>
      ) : (
        <div className="space-y-4">
          {docs.length ? (
        <Card className="divide-y divide-[#F0F2F5] py-1">
            {docs.map((d) => (
              <button
                key={d.id}
                className="flex w-full items-center gap-3 py-3 text-left"
                onClick={async () => window.open(await openMyDocument(d.id), "_blank", "noopener")}
              >
                <FileText size={18} color="#2563EB" />
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-medium text-[#101928]">{d.kind}</p>
                  <p className="truncate text-[12px] text-[#667185]">
                    …{d.cars.join(", …")} · sent {day(d.sent_on)}
                  </p>
                </div>
              </button>
            ))}
          </Card>
          ) : null}
          {uploads.length ? (
            <div>
              <p className="mb-2 px-1 text-[13px] font-medium text-[#667185]">From your team</p>
              <Card className="divide-y divide-[#F0F2F5] py-1">
                {uploads.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 py-3">
                    <FileText size={18} color="#865503" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] font-medium text-[#101928]">{u.kind}</p>
                      <p className="truncate text-[12px] text-[#667185]">
                        …{u.car} · {u.from} · {day(u.sent_on)}
                      </p>
                    </div>
                    <span className={`text-[12px] ${u.checked ? "text-[#036B26]" : "text-[#865503]"}`}>
                      {u.checked ? "Checked by Dockie" : "Dockie is checking"}
                    </span>
                  </div>
                ))}
              </Card>
            </div>
          ) : null}
        </div>
      )}
    </Page>
  );
}
